import { css } from '@emotion/css'
import React, { useContext } from 'react'
import { SelectedValueReportingFacet } from '../../graphql/schema/types/value-reporting'
import { Role } from '../../ng/bridge-utils/roles'
import { ErrorBoundary } from '../../legacy-design-system/error-boundary/error-boundary'
import { hasRole, getMostPermissiveRole } from '../roles'
import { useOrganizationFeature } from '../organization-feature'
import {
  useVrApiExportDialog,
  VrApiExportDialog
} from './export/vr-api-export-dialog'
import {
  getApplicationRatingCurlCommand,
  getRouteCoverageCurlCommand
} from './export/vr-curl-commands'
import { VrModuleRouteCoverage } from './modules/route-coverage'
import { VrHeader } from './header/vr-header'
import { VrModuleCodeSourceRating } from './modules/code-source-rating'
import { VrModuleList, VrModuleRow } from './modules/layout'
import {
  VrModuleLicenses,
  VrModuleLicensesProps
} from './modules/licenses/vr-module-licenses'
import { VrModuleOverallApplicationRating } from './modules/overall-app-rating'
import { VrModuleVulnerabilityHistogram } from './modules/vuln-histogram'
import { useIntl } from 'react-intl'
import { VrModuleOpenClosedVulns } from './modules/open-closed-vulns'
import { VrModuleVulnerabilityHistogramProps } from './modules/vuln-histogram/vr-module-vuln-histogram'
import { useValueReportingFilters } from './use-value-reporting-filters'
import { useRatings } from './api/use-ratings'
import { useCurrentUserProfile } from '../current-user/api/use-current-user-profile'
import { Serverless, SERVERLESS_MODULES } from '../serverless/serverless'
import { VrStandaloneModulePanel } from './modules/layout/vr-module-containers'
import { SplitContext } from '../../ng/splitio-wrapper'
import { useCurrentUser } from 'contrastBase/state'
import { useOrganization } from '../organization-licenses/api/use-organization'
import { VrModuleHeadlines } from './modules/headlines/vr-module-headlines'

export const showLicenseModule = ({ role }: { role?: Role }) =>
  role && hasRole(role, 'ADMIN')

export interface ValueReportingDashboardProps {
  apiKey: string
  hostUrl: string
  authHeader: string
  organizationUuid: string
  onBackToOldDashboard: () => void
}

interface VrModuleApiProps {
  apiKey: string
  authHeader: string
  hostUrl: string
  organizationUuid: string
  selectedFacets: SelectedValueReportingFacet[]
}

export function ValueReportingDashboard({
  organizationUuid,
  apiKey,
  authHeader,
  hostUrl,
  onBackToOldDashboard
}: ValueReportingDashboardProps) {
  const { currentUser } = useCurrentUser()

  const role = getMostPermissiveRole(currentUser?.roles ?? [])
  const organizationData = useOrganization({ organizationUuid })

  const { isSplitTreatmentOn } = useContext(SplitContext)
  const isServerlessOverviewSummaryOn = isSplitTreatmentOn(
    'CN-3878_serverless_summary_overview'
  )
  const ffDashboardHeadlines = isSplitTreatmentOn('PROD-2781_dashboard_news')
  const vulnTrendsGraphFeature = useOrganizationFeature(
    organizationUuid,
    'vulnerability-trends-graph'
  )
  const currentUserProfileData = useCurrentUserProfile({
    organizationUuid,
    enabled: !!organizationUuid
  })
  const protectEnabled =
    organizationData.data && organizationData.data.protectionEnabled

  const intl = useIntl()
  const {
    selectedFacets,
    aggregationSelections,
    onGroupToggle,
    onMultiGroupToggle,
    onOptionToggle,
    onAggregationCategoriesClear
  } = useValueReportingFilters()
  const ratingsData = useRatings({
    organizationUuid,
    selectedFacets,
    enabled: !organizationData.isLoading && !organizationData.isError
  })

  if (organizationData.isError) {
    throw new Error(
      'Value reporting dashboard caught an error trying to get the current organization'
    )
  }

  const isLoading = organizationData.isLoading || ratingsData.isLoading
  const { openDialog, dialogProps } = useVrApiExportDialog()
  const doShowLicenseModule = showLicenseModule({ role })
  const apiProps: VrModuleApiProps = {
    apiKey,
    authHeader,
    hostUrl,
    organizationUuid,
    selectedFacets
  }
  const licenseModuleProps: VrModuleLicensesProps = {
    ...apiProps,
    protectEnabled,
    onBackToOldDashboard
  }
  const vulnHistogramProps: VrModuleVulnerabilityHistogramProps = {
    ...apiProps,
    onBackToOldDashboard
  }

  return (
    <div
      className={css`
        position: relative;
        width: 100%;
      `}
      data-testid={`value-reporting-content-${
        isLoading ? 'loading' : 'loaded'
      }`}
    >
      <VrHeader
        organizationUuid={organizationUuid}
        aggregationSelections={aggregationSelections}
        selectedFacets={selectedFacets}
        ratingsLoading={isLoading}
        totalApplications={ratingsData.totalApplications}
        onGroupToggle={onGroupToggle}
        onMultiGroupToggle={onMultiGroupToggle}
        onOptionToggle={onOptionToggle}
        onAggregationCategoriesClear={onAggregationCategoriesClear}
        onBackToOldDashboard={onBackToOldDashboard}
      />
      <VrModuleList>
        <VrApiExportDialog
          {...dialogProps}
          titleId="title_applicationSecurityApi"
          sections={[
            {
              titleId: 'title_applicationOverallAndCodeSourceRating',
              sectionTestId: 'value-reporting-ratings',
              curlCommand: getApplicationRatingCurlCommand({
                protocolAndHost: hostUrl,
                organizationUuid,
                authHeader,
                apiKey,
                facets: selectedFacets
              })
            },
            {
              titleId: 'title_valueReporting_routesCovered',
              sectionTestId: 'value-reporting-route-coverage',
              curlCommand: getRouteCoverageCurlCommand({
                protocolAndHost: hostUrl,
                organizationUuid,
                authHeader,
                apiKey,
                facets: selectedFacets
              })
            }
          ]}
          e2eTestId="value-reporting-ratings-dialog"
        />

        <VrModuleRow
          order={1}
          onExportClick={openDialog}
          exportButtonE2eTestId="value-reporting-ratings-export-button"
        >
          <ErrorBoundary>
            <>
              <VrModuleOverallApplicationRating
                organizationUuid={organizationUuid}
                selectedFacets={selectedFacets}
                isDataLoading={isLoading}
                data={ratingsData.facets?.overall}
              />
              <VrModuleCodeSourceRating
                intl={intl}
                isDataLoading={isLoading}
                data={
                  ratingsData &&
                  ratingsData.facets && {
                    customCode: ratingsData.facets?.customCode,
                    libraries: ratingsData.facets?.libraries
                  }
                }
              />
              <VrModuleRouteCoverage
                parentIsLoading={isLoading}
                organizationUuid={organizationUuid}
                selectedFacets={selectedFacets}
              />
            </>
          </ErrorBoundary>
        </VrModuleRow>

        {isServerlessOverviewSummaryOn &&
          currentUserProfileData.currentUserProfile?.serverless && (
            <VrStandaloneModulePanel
              hasBorder={true}
              order={2}
              minHeight={300}
              testId="value-reporting-serverless-overview"
            >
              <Serverless
                module={SERVERLESS_MODULES.VR_RATING_OVERVIEW}
                selectedFacets={selectedFacets}
              />
            </VrStandaloneModulePanel>
          )}

        {vulnTrendsGraphFeature.enabled && (
          <VrModuleOpenClosedVulns {...apiProps} />
        )}

        {ffDashboardHeadlines && <VrModuleHeadlines />}

        <VrModuleVulnerabilityHistogram {...vulnHistogramProps} />
        {doShowLicenseModule && <VrModuleLicenses {...licenseModuleProps} />}
      </VrModuleList>
    </div>
  )
}
